import { useState, useRef, useCallback } from 'react';

const DIGIT = /^[0-9]$/;

/**
 * Entrada do código de 6 dígitos em caixas separadas. Notifica via `onChange`
 * e dispara `onComplete` ao completar.
 *
 * @param {object} params
 * @param {number} [params.length=6] quantidade de caixas
 * @param {(value: string) => void} [params.onChange]
 * @param {(value: string) => void} [params.onComplete]
 */
export default function useOtpInput({ length = 6, onChange, onComplete } = {}) {
  const [digits, setDigits] = useState(() => Array(length).fill(''));
  const inputsRef = useRef([]);

  const fieldsRef = useRef(
    Array.from({ length }, (_, position) => `otp-${position}`),
  );

  const focusAt = useCallback(index => {
    const el = inputsRef.current[index];
    if (el) el.focus();
  }, []);

  const notify = useCallback(
    nextDigits => {
      const value = nextDigits.join('');
      if (onChange) onChange(value);
      if (value.length === length && !nextDigits.includes('') && onComplete) {
        onComplete(value);
      }
    },
    [onChange, onComplete, length],
  );

  const commit = useCallback(
    nextDigits => {
      setDigits(nextDigits);
      notify(nextDigits);
    },
    [notify],
  );

  const handleChange = useCallback(
    (index, raw) => {
      const char = raw.slice(-1);
      if (char && !DIGIT.test(char)) return;
      const next = [...digits];
      next[index] = char;
      commit(next);
      if (char && index < length - 1) focusAt(index + 1);
    },
    [digits, commit, length, focusAt],
  );

  const handleKeyDown = useCallback(
    (index, event) => {
      if (event.key === 'Backspace') {
        const next = [...digits];
        if (next[index]) {
          next[index] = '';
          commit(next);
        } else if (index > 0) {
          next[index - 1] = '';
          commit(next);
          focusAt(index - 1);
        }
      }
      if (event.key === 'ArrowLeft' && index > 0) focusAt(index - 1);
      if (event.key === 'ArrowRight' && index < length - 1) focusAt(index + 1);
    },
    [digits, commit, length, focusAt],
  );

  const handlePaste = useCallback(
    event => {
      const pasted = (event.clipboardData.getData('text') || '')
        .replace(/\D/g, '')
        .slice(0, length);
      if (!pasted) return;
      event.preventDefault();
      const next = Array(length).fill('');
      pasted.split('').forEach((char, i) => {
        next[i] = char;
      });
      commit(next);
      focusAt(Math.min(pasted.length, length - 1));
    },
    [length, commit, focusAt],
  );

  const clear = useCallback(() => {
    commit(Array(length).fill(''));
    focusAt(0);
  }, [length, commit, focusAt]);

  const registerRef = useCallback(
    index => el => {
      inputsRef.current[index] = el;
    },
    [],
  );

  return {
    digits,
    value: digits.join(''),
    fields: fieldsRef.current,
    handleChange,
    handleKeyDown,
    handlePaste,
    clear,
    registerRef,
    focusAt,
  };
}
